import React, { useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import {
    ANALYTIC_NAME_FIELD,
    ANTENNA_NAME_FIELD,
    BUDGET_LINE_CODE,
    CHAPTER_FIELD,
    FUNCTION_LABEL_FIELD,
    GESTIONNAIRE_FIELD,
    INSCRIPTION_ID,
    NATURE_LABEL_FIELD,
    OPERATION_NAME_FIELD,
    ORG_CHART_FINANCE_FIELD,
    REAL_ORDER_FIELD,
    SUB_NATURE_LABEL_FIELD,
    SUPER_OPERATION_NAME_FIELD,
} from 'routes/BudgetModule/config/constants';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import PanelDetails from 'ui/Panel/PanelDetails';
import { useTranslation } from 'react-i18next';
import { ImputationDimensionsContext } from 'routes/BudgetModule/components/BudgetWithProvider/ImputationDimensionsContext';
import { IntegrationContext } from 'routes/BudgetModule/components/BudgetWithProvider/IntegrationContext';
import { isCustomBudgetProposal } from 'routes/BudgetModule/components/BudgetProposal/Resource/hooks/budgetProposal';
import {
    EXCLUDED_DIMENSIONS_BY_CONTEXT,
    getImputationDimensionsLabels,
} from 'routes/BudgetModule/config/hooks';
import { translateBlockValue } from 'i18n';
import { useGetAllInscriptionsEvent } from './hooks/inscriptionHistory';
import Loading from 'components/Loading';
import {
    EventTypes,
    FullInscriptionEventT,
    HistoryUpdateContentT,
} from './types';
import CreateEvent from './components/HistoryItems/CreateEvent';
import UpdateEvent from './components/HistoryItems/UpdateEvent';
import SubCreateEvent from './components/HistoryItems/SubCreateEvent';
import CommentEvent from './components/HistoryItems/CommentEvent';
import CommentField from './components/HistoryItems/CommentField';
import { useSelector } from 'store/hooks';
import Overview from './components/Overview';
import NoDataState from 'ui/NoDataState/NoDataState';

type Props = {
    onClose: () => void;
    open: boolean;
    budgetProposal: BudgetProposalT;
    inscriptionId: number;
    queryCacheKey: InscriptionQueryKey;
};

const ORDERED_IMPUTATION_DIMENSIONS: Array<keyof InscriptionT> = [
    GESTIONNAIRE_FIELD,
    ORG_CHART_FINANCE_FIELD,
    CHAPTER_FIELD,
    NATURE_LABEL_FIELD,
    SUB_NATURE_LABEL_FIELD,
    FUNCTION_LABEL_FIELD,
    ANTENNA_NAME_FIELD,
    OPERATION_NAME_FIELD,
    SUPER_OPERATION_NAME_FIELD,
    ANALYTIC_NAME_FIELD,
    REAL_ORDER_FIELD,
    BUDGET_LINE_CODE,
];

const getEventComponent = (
    event: FullInscriptionEventT,
): React.ReactElement => {
    switch (event.type) {
        case EventTypes.CREATE:
            return <CreateEvent {...event} />;
        case EventTypes.UPDATE:
            return (
                <UpdateEvent
                    {...(event as FullInscriptionEventT & {
                        content: HistoryUpdateContentT;
                    })}
                />
            );
        case EventTypes.SUBCREATE:
            return <SubCreateEvent {...event} />;
        case EventTypes.COMMENT:
            return (
                <CommentEvent
                    {...(event as FullInscriptionEventT & {
                        content: string;
                    })}
                />
            );
        default: {
            // never reached, but needed for TypeScript to understand that the switch is exhaustive
            const _exhaustiveCheck: never = event.type;
            return _exhaustiveCheck;
        }
    }
};

export default function InscriptionsDetails({
    open,
    onClose,
    inscriptionId,
    budgetProposal,
    queryCacheKey,
}: Props): React.ReactElement {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const loggedInUserEmail = useSelector((state) => state.vision.user.email);

    const inscriptionData = queryClient
        .getQueryData<{ data: InscriptionT[] }>(queryCacheKey)
        ?.data.find((e) => e[INSCRIPTION_ID] === inscriptionId);

    const imputationRawDimensions = useContext(ImputationDimensionsContext);

    // SCHOOL --
    const integrations = useContext(IntegrationContext);
    const isSchool = isCustomBudgetProposal(integrations);

    const imputationDimensions = getImputationDimensionsLabels(
        imputationRawDimensions,
        [
            ...EXCLUDED_DIMENSIONS_BY_CONTEXT.BudgetProposal,
            ...EXCLUDED_DIMENSIONS_BY_CONTEXT.ExpenseIncome,
        ],
        isSchool,
    );
    const orderedImputationDimensions = (
        imputationDimensions as Array<keyof InscriptionT>
    ).sort(
        (a, b) =>
            ORDERED_IMPUTATION_DIMENSIONS.indexOf(a) -
            ORDERED_IMPUTATION_DIMENSIONS.indexOf(b),
    );

    const { events, isLoading } = useGetAllInscriptionsEvent(
        inscriptionData,
        budgetProposal,
    );

    if (isLoading) return <Loading />;
    if (!inscriptionData) {
        return <NoDataState />;
    }

    const valuesToDisplay = [
        {
            translation: translateBlockValue('budget.name', true),
            value: budgetProposal.budgetName,
        },
        ...orderedImputationDimensions.map((value) => ({
            translation: translateBlockValue(value, true),
            value: inscriptionData[value] as string,
        })),
    ];

    return (
        <PanelDetails
            parentTitle={t('budgetHistory.parentImputation')}
            valuesToDisplay={valuesToDisplay}
            onClose={onClose}
            headerTitle={t('budgetHistory.sidePanelTitle')}
            open={open}
            events={events}
            getEventComponent={getEventComponent}
            overview={
                <Overview
                    inscriptionData={inscriptionData}
                    budgetProposal={budgetProposal}
                />
            }
            commentField={
                <CommentField
                    isLastItem={!(events && events.length > 0)}
                    inscriptionId={inscriptionData[INSCRIPTION_ID]}
                    loggedInUserEmail={loggedInUserEmail}
                />
            }
            reverseParentBold
            drawerWidth="100%"
        />
    );
}
